import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faDownload, faFile } from "@fortawesome/free-solid-svg-icons";

import PaginatorComponent from "../../general/auxiliar/ui/PaginatorComponent";
import ModalConfirm from "../../general/auxiliar/modal/ModalConfirm";
import UploadDocumentPopup from "./UploadDocumentPopup";
import LoadingTable from "../../general/auxiliar/table/LoadingTable";
import OverviewTable from "../../general/auxiliar/table/OverviewTable";
import usePaginationFilterEffects from "../../../auxiliar/customHooks/usePaginationFilterEffects";
import { getAllDocuments, deleteDocument } from "../../../actions/document";
import {
    handleSubmit,
    handleSubmitWithDispatch,
    getTableState,
} from "../../../auxiliar/auxFunctions";

const OverviewDocuments = () => {
    const modalId = "delete-document-modal";

    const dispatch = useDispatch();
    const { patient_id: patientId } = useParams();
    const { id: activeClinicId } = useSelector((state) => state.activeClinic);

    const [documents, setDocuments] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        per_page: 3,
        page: 1,
        clinic_id: activeClinicId,
        patient_id: patientId,
        order: "upload_date",
    });

    const [refreshBool, setRefreshBool] = useState(false);
    const refresh = () => {
        if (filter.page > 1 && documents?.order?.length === 1 && !documents?.pagination?.has_next) {
            setFilter({ ...filter, page: filter.page - 1 });
        }
        setRefreshBool(!refreshBool);
    };

    const disabled = usePaginationFilterEffects(
        filter,
        undefined,
        getAllDocuments,
        setDocuments,
        setFilter,
        refreshBool,
    );

    const [deleteId, setDeleteId] = useState();
    const deleteDoc = (id) => {
        setDeleteId(id);
        document.getElementById(modalId)?.showModal();
    };

    // Table columns configuration
    const columns = [
        { label: "Documento", icon: faFile, width: "w-[65%]" },
        { label: "Fecha", icon: null, cellClass: "hidden lg:table-cell", width: "w-[25%]" },
    ];

    // Function to get row data for table
    const getRowData = (doc, id) => [
        <div
            key={`${id}-name`}
            className="text-sm font-medium text-base-content truncate"
            title={doc.file_name}
        >
            {doc.file_name}
        </div>,
        <span key={`${id}-date`} className="text-sm text-base-content/70">
            {doc.upload_date}
        </span>,
    ];

    // Render actions for each row
    const renderActions = (doc, id) => (
        <div className="flex gap-1 justify-center">
            <a href={doc.download_url} download={doc.file_name}>
                <button type="button" className="btn btn-primary btn-xs">
                    <FontAwesomeIcon icon={faDownload} />
                </button>
            </a>
            <button
                type="button"
                className="btn btn-error btn-xs"
                onClick={handleSubmit(deleteDoc, id)}
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        </div>
    );

    const tableState = getTableState(documents);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando documentos..." />;

            case "empty":
                return (
                    <div className="bg-base-100 rounded-xl shadow-sm border border-base-300 p-8">
                        <div className="text-center">
                            <p className="text-base-content/70 mb-4">
                                Empieza a almacenar los documentos del paciente aquí
                            </p>
                            <UploadDocumentPopup refresh={refresh} />
                        </div>
                    </div>
                );

            case "hasData":
            default:
                return (
                    <OverviewTable
                        data={documents}
                        columns={columns}
                        getRowData={getRowData}
                        renderActions={renderActions}
                        actionsWidth="w-[30%]"
                        className="!block"
                    />
                );
        }
    };

    return (
        <>
            <div>
                <div className="my-2 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                    {/* Upload button */}
                    <UploadDocumentPopup refresh={refresh} />

                    {/* Pagination */}
                    <div className="w-full sm:w-auto">
                        <PaginatorComponent
                            pagination={documents.pagination}
                            setFilter={setFilter}
                            filter={filter}
                            disabled={disabled}
                        />
                    </div>
                </div>

                {renderContent()}
            </div>

            <ModalConfirm
                id={modalId}
                title="¿Estás seguro de que quieres eliminar el documento?"
                text="No podremos recuperar la información más adelante."
                onConfirm={handleSubmitWithDispatch(
                    dispatch,
                    deleteDocument,
                    deleteId,
                    modalId,
                    refresh,
                )}
            />
        </>
    );
};

export default OverviewDocuments;
